import { useState, createContext, useEffect, useCallback } from "react";
import { supabase } from "./services/supabaseClient";
import { t } from 'i18next';
import { debugLogger } from "./utils/debugLogger";

export const GlobalStates = createContext();

const TaskContextProvider = (props) => {
    const [tasks, setTasks] = useState('login');
    const [user, setUser] = useState(null);
    const [userStatus, setUserStatus] = useState(null);
    const [token, setToken] = useState('');
    const [isLoading, setIsLoading] = useState(true); // Auth loading state
    const [type, setType] = useState('');
    const [alert, setAlert] = useState('');
    const [languageG, setLanguageG] = useState('');
    const [path, setPath] = useState((process.env.REACT_APP_SUPABASE_URL || 'https://kpiugrrylywgqcldmioz.supabase.co') + '/');
    const [pathImg, setPathImg] = useState((process.env.REACT_APP_SUPABASE_URL || 'https://kpiugrrylywgqcldmioz.supabase.co') + '/');

    // New states for session-specific data
    const [templates, setTemplates] = useState([]);
    const [userForms, setUserForms] = useState([]);
    const [filledForms, setFilledForms] = useState([]);
    const [isSessionDataLoading, setIsSessionDataLoading] = useState(false);
    const [sessionDataError, setSessionDataError] = useState(null);
    const [sessionDataLoaded, setSessionDataLoaded] = useState(false); // Flag to prevent multiple loads

    // --- Data Loading Functions --- 

    const loadTemplates = useCallback(async () => {
        try {
            const { data, error } = await supabase
                .from('formos_tipai')
                .select('*');

            if (error) throw error;

            const formattedData = data.map(item => ({
                id: item.id,
                attributes: {
                    name: item.pavadinimas,
                    description: item.aprasymas,
                    type: item.tipas,
                    validFrom: item.galioja_nuo,
                    validTo: item.galioja_iki,
                    questions: item.klausimai,
                    template_id: item.template_id
                }
            }));
            return formattedData;
        } catch (err) {
            return []; // Return empty array on error
        }
    }, []);

    const loadUserForms = useCallback(async (companyName) => {
        if (!companyName) return [];
        try {
            const { data, error } = await supabase
                .from('formos')
                .select('*')
                .eq('imones_pavadinimas', companyName);

            if (error) throw error;

            // Keep original structure for now, adjust in component if needed
            return data; 
        } catch (err) {
            return []; // Return empty array on error
        }
    }, []);

    const getUzpildytosFormos = useCallback(async (companyName) => {
        if (!companyName) return [];
        try {
            const { data, error } = await supabase
                .from('uzpildytos_imones')
                .select('*')
                .eq('imones_pavadinimas', companyName);

            if (error) throw error;
            // Keep original structure
            return data; 
        } catch (err) {
            return []; // Return empty array on error
        }
    }, []);

    const loadAllSessionData = useCallback(async (companyName) => {
        if (!companyName) {
            return;
        }
        if (sessionDataLoaded || isSessionDataLoading) {
            return; 
        }

        setIsSessionDataLoading(true);
        setSessionDataError(null);
        setSessionDataLoaded(false);

        try {
            const [templatesData, userFormsData, filledFormsData] = await Promise.all([
                loadTemplates(),
                loadUserForms(companyName),
                getUzpildytosFormos(companyName)
            ]);

            setTemplates(templatesData || []);
            setUserForms(userFormsData || []);
            setFilledForms(filledFormsData || []);
            setSessionDataLoaded(true);

        } catch (error) {
            setSessionDataError(error.message || 'Failed to load session data');
            setTemplates([]);
            setUserForms([]);
            setFilledForms([]);
            setSessionDataLoaded(false); 
        } finally {
            setIsSessionDataLoading(false);
        }
    }, [sessionDataLoaded, isSessionDataLoading, loadTemplates, loadUserForms, getUzpildytosFormos]);

    // --- Authentication Setup Effect ---
    useEffect(() => {
        let isMounted = true;
        let authSubscription = null;
        let authTimeoutId = null;
        let initialCheckDone = false;
        let isSigningOut = false; // Flag to prevent sign out loops
        let lastCheckedUserId = null; // Cache the last checked user ID
        let lastCheckTime = 0; // Track when we last checked
        const CHECK_INTERVAL = 300000; // Only recheck every 5 minutes

        // Timeout to automatically clear loading state if auth check takes too long
        const MAX_AUTH_CHECK_TIME = 5000; // 5 seconds

        // Process auth state
        const processAuthState = async (currentSession, eventType) => {
            try {
                if (!isMounted) return;
                
                // If we're already signing out, don't process further to prevent loops
                if (isSigningOut && !currentSession) return;

                let currentUser = null;
                let currentStatus = null;
                let currentToken = '';

                if (currentSession?.user) {
                    // User is authenticated
                    currentUser = currentSession.user;
                    currentToken = currentSession.access_token || '';

                    // Check if we should skip database checks (same user, checked recently)
                    const now = Date.now();
                    const shouldSkipCheck = 
                        lastCheckedUserId === currentSession.user.id && 
                        (now - lastCheckTime) < CHECK_INTERVAL &&
                        eventType !== 'SIGNED_IN'; // Always check on fresh sign in

                    if (shouldSkipCheck) {
                        // Use cached values - just update token if needed
                        if (isMounted) {
                            setToken(currentToken);
                        }
                        return;
                    }

                    try {
                        // Track API call for debugging
                        debugLogger.trackApiCall('users.select');
                        
                        // First check user's profile data
                        const { data: userData, error: userError } = await supabase
                            .from('users')
                            .select('confirmed, valid_to')
                            .eq('id', currentSession.user.id)
                            .single();

                        if (userError) {
                            console.error("Error fetching user data:", userError);
                            // Sign out if we can't verify user status
                            if (!isSigningOut) {
                                isSigningOut = true;
                                await supabase.auth.signOut();
                                isSigningOut = false;
                            }
                            currentUser = null;
                            currentToken = '';
                            return;
                        }

                        // Check if user is confirmed
                        if (!userData?.confirmed) {
                            console.warn("User not confirmed - signing out");
                            if (!isSigningOut) {
                                isSigningOut = true;
                                await supabase.auth.signOut();
                                isSigningOut = false;
                            }
                            currentUser = null;
                            currentToken = '';
                            setAlert && setAlert(t("accComfirmedError"));
                            return;
                        }

                        // Check if subscription is valid
                        const isValidSubscription = userData.valid_to && new Date(userData.valid_to) >= new Date();
                        if (!isValidSubscription) {
                            console.warn("User subscription expired - signing out");
                            if (!isSigningOut) {
                                isSigningOut = true;
                                await supabase.auth.signOut();
                                isSigningOut = false;
                            }
                            currentUser = null;
                            currentToken = '';
                            setAlert && setAlert(t("accValidToError"));
                            return;
                        }

                        // Track API call for debugging
                        debugLogger.trackApiCall('check_account_status');
                        
                        // If user passed all checks, try to get additional status
                        const { data: statusData, error: rpcError } = await supabase.rpc('check_account_status', {
                            user_id: currentSession.user.id
                        });

                        if (rpcError) {
                            console.error("Error checking account status:", rpcError);
                            // Don't clear user on RPC error if basic checks passed
                        } else {
                            currentStatus = statusData?.[0] || null;
                        }

                        // Update cache
                        lastCheckedUserId = currentSession.user.id;
                        lastCheckTime = now;
                    } catch (error) {
                        console.error("Error in auth state processing:", error);
                        // Sign out on any error to be safe
                        if (!isSigningOut) {
                            isSigningOut = true;
                            await supabase.auth.signOut();
                            isSigningOut = false;
                        }
                        currentUser = null;
                        currentToken = '';
                    }
                } else {
                    // User is not authenticated
                    currentUser = null;
                    currentStatus = null;
                    currentToken = '';
                    lastCheckedUserId = null;
                    lastCheckTime = 0;
                }

                // Update state based on derived values
                if (isMounted) {
                    setUser(currentUser);
                    setUserStatus(currentStatus);
                    setToken(currentToken);

                    // Set loading to false after processing is complete
                    setIsLoading(false);
                    initialCheckDone = true;

                }
            } catch (error) {
                console.error("Error processing auth state:", error);
                if (isMounted) {
                    setUser(null);
                    setUserStatus(null);
                    setToken('');
                    setIsLoading(false);
                    initialCheckDone = true;
                }
            }
        };

        // Set up auth state listener
        const setupAuth = async () => {
            try {
                // Safety timeout - ensure loading state gets cleared even if auth check fails
                authTimeoutId = setTimeout(() => {
                    if (isMounted && !initialCheckDone) {
                        console.warn("Auth check timeout - forcing loading state to complete");
                        setIsLoading(false);
                        initialCheckDone = true;
                        document.documentElement.classList.remove("auth-loading");
                    }
                }, MAX_AUTH_CHECK_TIME);

                // Get current session first
                const { data: initialData } = await supabase.auth.getSession();

                // Process the initial session
                if (initialData?.session) {
                    await processAuthState(initialData.session);
                }

                // Subscribe to auth state changes
                const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
                    if (!isMounted) return;

                    // Process session from auth state change
                    await processAuthState(session, event);
                });

                authSubscription = subscription;

            } catch (error) {
                console.error("Auth setup error:", error);
                if (isMounted) {
                    setUser(null);
                    setUserStatus(null);
                    setToken('');
                    setIsLoading(false);
                    initialCheckDone = true;
                }
            } finally {
                // Clear the safety timeout if we've processed the auth state
                if (authTimeoutId) {
                    clearTimeout(authTimeoutId);
                }
            }
        };

        setupAuth();

        return () => {
            isMounted = false;
            if (authTimeoutId) {
                clearTimeout(authTimeoutId);
            }
            if (authSubscription) {
                authSubscription.unsubscribe();
            }
        };
    }, []);

    // --- Session Data Loading Effect --- (Depends on user metadata)
    useEffect(() => {
        const userId = user?.id;
        const companyName = user?.user_metadata?.imones_pavadinimas;

        if (companyName && !sessionDataLoaded && !isSessionDataLoading) {
            loadAllSessionData(companyName);
        } else if (!companyName && sessionDataLoaded) {
            setTemplates([]);
            setUserForms([]);
            setFilledForms([]);
            setSessionDataLoaded(false); 
            setSessionDataError(null);
        } else if (userId && !companyName && !sessionDataLoaded && !isSessionDataLoading) {
            // User exists but company name missing. Waiting...
        } else if (!userId && sessionDataLoaded) { 
            setTemplates([]);
            setUserForms([]);
            setFilledForms([]);
            setSessionDataLoaded(false); 
            setSessionDataError(null);
        }

    }, [user?.id, user?.user_metadata?.imones_pavadinimas, sessionDataLoaded, isSessionDataLoading, loadAllSessionData]);

    return (
        <GlobalStates.Provider value={{
            tasks, setTasks,
            user, setUser,
            token, setToken,
            path, setPath,
            pathImg, setPathImg,
            isLoading, 
            type, setType,
            alert, setAlert,
            languageG, setLanguageG,
            templates,
            userForms,
            filledForms,
            isSessionDataLoading, 
            sessionDataError,
            userStatus
        }}>
            {props.children}
        </GlobalStates.Provider>
    );
};

export default TaskContextProvider;
